/**
 * App Events - Central Event Hub
 *
 * Subscribes to all pub/sub topics and handles application-wide events.
 * Acts as the main event listener and coordinator.
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';
import { showToast } from './toast.js';
import { initNavGeneration, setStaleContextRecovery, setDeadSessionHandler } from './navGeneration.js';
import AuthController from '../services/AuthController.js';

/**
 * Global Web Component Event Bridge
 * Listens to all custom events from web components and publishes to PubSub
 *
 * Web components can include a 'pubsubTopic' property in event.detail
 * to specify which PubSub topic to publish to.
 */
function initWebComponentEventBridge() {
  // Generic event handler for all web component custom events
  const handleWebComponentEvent = (eventType) => {
    return (e) => {
      const topic = e.detail?.pubsubTopic;

      if (topic) {
        // If web component specifies a topic, use it
        PubSub.publish(topic, e.detail);
      }
    };
  };

  // Register listeners for all web component event types
  document.addEventListener('nav-select', handleWebComponentEvent('nav-select'));
  document.addEventListener('logo-click', handleWebComponentEvent('logo-click'));
  document.addEventListener('nav-hover', handleWebComponentEvent('nav-hover'));
  document.addEventListener('nav-double-tap', handleWebComponentEvent('nav-double-tap'));
  document.addEventListener('nav-long-tap', handleWebComponentEvent('nav-long-tap'));
  document.addEventListener('message-sent', handleWebComponentEvent('message-sent'));
}

/**
 * Initialize application event listeners
 */
export function initAppEvents() {
  console.log('[AppEvents] Initializing event listeners...');

  // Navigation-scoped request discarding. Started here because this is the app's single
  // init hub, but deliberately NOT driven by an AppBroadcast topic: pubsub-js delivers
  // with setTimeout(deliver, 0), and both halves of this guard have to be synchronous
  // with navigation or they act on the wrong page. See navGeneration.js's header.
  initNavGeneration();

  // Stale org context (403 + code 'invalid_active_org') recovers by re-resolving scope.
  // fetchScope() ALREADY clears a stored org the server did not honour and republishes
  // AUTH.SCOPE.LOADED — reusing it rather than writing a second copy of that clearing is
  // deliberate: a duplicated check is what let the server-side !scope fix miss its second
  // copy. Registered rather than imported by navGeneration, which is upstream of this.
  setStaleContextRecovery(() => AuthController.fetchScope());

  // Dead session (401 through guardedFetch): the token is gone or expired, so
  // clear authentication and return to sign-in. The feature editor holds its
  // working copy outside the page until fresh authentication and access checks;
  // ordinary routes retain the existing signout/reload behavior.
  setDeadSessionHandler(() => AuthController.doSignout(true));

  // ============================================
  // Application Events
  // ============================================

  PubSub.subscribe(AppBroadcast.APP_READY, (msg, data) => {
    console.log('[AppEvents] APP_READY:', data);
  });

  PubSub.subscribe(AppBroadcast.APP_ERROR, (msg, data) => {
    console.error('[AppEvents] APP_ERROR:', data);
    // TODO: Show error toast/modal
  });

  PubSub.subscribe(AppBroadcast.APP_SIGNOUT, (msg, data) => {
    console.log('[AppEvents] APP_SIGNOUT:', data);
    // Handled by mvvLegit.doSignout()
  });

  // ============================================
  // Route Navigation Events
  // ============================================

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_BEFORE, (msg, data) => {
    console.log('[AppEvents] ROUTE_NAV_BEFORE:', data);
    // Show loading indicator
    PubSub.publish(AppBroadcast.UI_LOADING_START);
  });

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_AFTER, (msg, data) => {
    console.log('[AppEvents] ROUTE_NAV_AFTER:', data);
    // Hide loading indicator
    PubSub.publish(AppBroadcast.UI_LOADING_END);
  });

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_ERROR, (msg, data) => {
    console.error('[AppEvents] ROUTE_NAV_ERROR:', data);
    PubSub.publish(AppBroadcast.APP_ERROR, {
      message: 'Navigation error',
      details: data
    });
  });

  // ============================================
  // Authentication Events
  // ============================================

  PubSub.subscribe(AppBroadcast.AUTH_SIGNIN_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNIN_SUCCESS:', data);
    // mvvLegit will handle navigation to home route
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNIN_FAIL, (msg, data) => {
    console.error('[AppEvents] AUTH_SIGNIN_FAIL:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: data.message || 'Sign in failed',
      type: 'danger'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNUP_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Account created! Please check your email to verify.',
      type: 'success'
    });
    // Navigate to verification link prompt page
    PubSub.publish(AppBroadcast.AUTH_VERIFY_LINK);
  });

  PubSub.subscribe(AppBroadcast.AUTH_VERIFY_LINK, (msg, data) => {
    console.log('[AppEvents] AUTH_VERIFY_LINK: Navigating to verf-link route');
    if (window.router) {
      window.router.navigate('/verf-link');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP_FAIL, (msg, data) => {
    console.error('[AppEvents] AUTH_SIGNUP_FAIL:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: data.message || 'Sign up failed',
      type: 'danger'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNOUT_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNOUT_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Signed out successfully',
      type: 'info'
    });
  });

  // REMOVED: This event handler was causing unwanted redirect to signin after successful signin
  // Navigation to signin is handled by AuthController when needed (e.g., token expiry)
  // PubSub.subscribe(AppBroadcast.AUTH_SIGNIN, (msg, data) => {
  //   console.log('[AppEvents] AUTH_SIGNIN: Navigating to signin route');
  //   if (window.router) {
  //     window.router.navigate('/signin');
  //   }
  // });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNUP: Navigating to signup route');
    if (window.router) {
      window.router.navigate('/signup');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_FORGOT, (msg, data) => {
    console.log('[AppEvents] AUTH_FORGOT: Navigating to forgot route');
    if (window.router) {
      window.router.navigate('/forgot');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_FORGOT_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_FORGOT_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Password reset email sent!',
      type: 'success'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_RESETHASH_NAV, (msg, data) => {
    console.log('[AppEvents] AUTH_RESETHASH_NAV: Navigating to resethash route');
    if (window.router) {
      window.router.navigate('/resethash');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_RESETHASH_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_RESETHASH_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Password reset successfully! Please sign in.',
      type: 'success'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_TOKEN_EXPIRED, (msg, data) => {
    console.warn('[AppEvents] AUTH_TOKEN_EXPIRED:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Session expired. Please sign in again.',
      type: 'warning'
    });
    // mvvLegit will handle automatic sign out and navigation
  });

  // ============================================
  // I18N Events
  // ============================================

  PubSub.subscribe(AppBroadcast.I18N_SET_LANG, (msg, data) => {
    console.log('[AppEvents] I18N_SET_LANG:', data);

    const { langCode } = data;

    if (!langCode) {
      console.error('[AppEvents] No langCode provided in I18N_SET_LANG event');
      return;
    }

    // Call I18nController to change language
    if (window.I18nController && typeof window.I18nController.setLanguage === 'function') {
      try {
        window.I18nController.setLanguage(langCode);
        console.log('[AppEvents] Language changed successfully to:', langCode);

        // Optionally publish success event
        PubSub.publish(AppBroadcast.UI_TOAST, {
          message: `Language changed to ${langCode}`,
          type: 'success'
        });
      } catch (error) {
        console.error('[AppEvents] Error changing language:', error);
        PubSub.publish(AppBroadcast.UI_TOAST, {
          message: `Failed to change language: ${error.message}`,
          type: 'danger'
        });
      }
    } else {
      console.error('[AppEvents] I18nController not available');
    }
  });

  // ============================================
  // CONVERSATION Events
  // ============================================

  // High-level subscriber for CONVERSATION.PROMPT with switch for sub-topics
  PubSub.subscribe('CONVERSATION.PROMPT', (msg, data) => {
    // Extract sub-topic from message (e.g., "CONVERSATION.PROMPT.CANVAS" -> "CANVAS")
    const subTopic = msg.split('.')[2];

    console.log(`[AppEvents] CONVERSATION.PROMPT.${subTopic}:`, data);

    if (!data || !data.message) {
      console.warn('[AppEvents] CONVERSATION.PROMPT received without message data');
      return;
    }

    switch(subTopic) {
      case 'CANVAS':
        // Display user prompt in chat canvas only
        console.log('[AppEvents] Display prompt in canvas:', data.message);
        addChatBubbleToCanvas(data.message, 'user', data.timestamp);
        PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
        break;

      case 'AGENT':
        // LLM agent removed - placeholder for future agent implementation
        console.log('[AppEvents] Agent prompt (no implementation):', data.message);
        break;

      case 'AGENT_CANVAS':
        // Display message in canvas (LLM agent and CLI removed)
        console.log('[AppEvents] Display message in canvas:', data.message);

        // Display user message in canvas
        addChatBubbleToCanvas(data.message, 'user', data.timestamp);
        PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);

        // LLM agent and CLI removed - placeholder for future agent implementation
        break;

      case 'STAGE':
        // Stage prompt in input area for user editing
        console.log('[AppEvents] Stage prompt for editing:', data.message);
        // TODO: Populate prompt input field
        break;

      default:
        console.log(`[AppEvents] Unhandled CONVERSATION.PROMPT sub-topic: ${subTopic}`);
        break;
    }
  });

  // High-level subscriber for CONVERSATION.RESPONSE with switch for sub-topics
  PubSub.subscribe('CONVERSATION.RESPONSE', (msg, data) => {
    // Extract sub-topic from message (e.g., "CONVERSATION.RESPONSE.CANVAS" -> "CANVAS")
    const subTopic = msg.split('.')[2];

    console.log(`[AppEvents] CONVERSATION.RESPONSE.${subTopic}:`, data);

    if (!data || !data.message) {
      console.warn('[AppEvents] CONVERSATION.RESPONSE received without message data');
      return;
    }

    switch(subTopic) {
      case 'CANVAS':
        // Display assistant response in chat canvas
        console.log('[AppEvents] Display response in canvas:', data.message);
        addChatBubbleToCanvas(data.message, 'assistant', data.timestamp, data.streaming);
        break;

      case 'WORKFLOW':
        // Send response to workflow engine
        console.log('[AppEvents] Send response to workflow engine:', data.message);
        // TODO: Implement workflow engine integration
        break;

      default:
        console.log(`[AppEvents] Unhandled CONVERSATION.RESPONSE sub-topic: ${subTopic}`);
        break;
    }
  });

  // ============================================
  // UI Events
  // ============================================

  PubSub.subscribe(AppBroadcast.UI_LOADING_START, (msg, data) => {
    // Show loading indicator
    const loader = document.getElementById('app-loader');
    if (loader) loader.classList.add('active');
  });

  PubSub.subscribe(AppBroadcast.UI_LOADING_END, (msg, data) => {
    // Hide loading indicator
    const loader = document.getElementById('app-loader');
    if (loader) loader.classList.remove('active');
  });

  PubSub.subscribe(AppBroadcast.UI_TOAST, (msg, data) => {
    showToast(data);
  });

  PubSub.subscribe(AppBroadcast.UI_WELCOME_BACK_CLICKED, () => {
    console.log('[AppEvents] UI_WELCOME_BACK_CLICKED');
    const sMsg = '0.1'; // TODO: get this from meta
    const curYear = new Date().getFullYear();
    if (sMsg && typeof neodigmToast !== 'undefined') {
      neodigmToast.q(`${sMsg} __devops_ts__|© ${curYear} Mach Five Tech`, 'night');
    }
  });

  // ============================================
  // Web Component Events Bridge
  // ============================================

  // Listen to all custom events from web components and bridge to PubSub
  initWebComponentEventBridge();

  // ============================================
  // M5T Primary Navigation Events
  // ============================================

  // Single high-level subscriber with switch for sub-topics
  PubSub.subscribe('M5T_PRIMARY_NAV', (msg, data) => {
    // Extract sub-topic from message (e.g., "M5T_PRIMARY_NAV.TAP" -> "TAP")
    const subTopic = msg.split('.')[1];

    //console.log(`[AppEvents] M5T_PRIMARY_NAV.${subTopic}:`, data);

    switch(subTopic) {
      case 'TAP':
        handlePrimaryNavTap(data);
        break;

      case 'LOGO_TAP':
        handlePrimaryNavLogoTap(data);
        break;

      case 'HOVER':
        // Handle hover events if needed
        break;

      case 'DOUBLE_TAP':
        // Handle double tap events if needed
        break;

      case 'LONG_TAP':
        // Handle long tap events if needed
        break;

      default:
        console.log(`[AppEvents] Unhandled M5T_PRIMARY_NAV sub-topic: ${subTopic}`);
        break;
    }
  });

  // Handler for navigation tap events
  function handlePrimaryNavTap(data) {
    const { token, level, primaryToken } = data;

    switch(token) {
      // Profile secondary navigation - Password Reset
      case 'RESET_PASSWORD':
        console.log('[AppEvents] Password reset requested');
        PubSub.publish(AppBroadcast.AUTH_RESETHASH_NAV);
        break;

      // Profile secondary navigation - Sign Out
      case 'SIGN_OUT':
        console.log('[AppEvents] Sign out requested');
        if (window.router) {
          window.router.navigate('/signout');
        }
        break;

      // Default handler - log for debugging
      default:
        console.log(`[AppEvents] Navigation: ${level} -> ${token}`, { primaryToken });
        break;
    }
  }

  // Handler for logo tap events
  function handlePrimaryNavLogoTap(data) {
    const sMsg = '0.1'; // TODO: get this from meta
    const curYear = new Date().getFullYear();
    if (sMsg && typeof neodigmToast !== 'undefined') {
      neodigmToast.q(`${sMsg} __devops_ts__|© ${curYear} Mach Five Tech`, 'night', 5000);
    }
  }

  // ============================================
  // Cleanup Events
  // ============================================

  // Clear session_user from localStorage on signout
  PubSub.subscribe(AppBroadcast.AUTH_SIGNOUT, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNOUT: Clearing session_user from localStorage');
    localStorage.removeItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER);
  });

  console.log('[AppEvents] Event listeners initialized');
}

// ============================================
// Chat Canvas Helpers
// ============================================

/**
 * Add or update chat bubble in canvas DOM
 * Supports streaming updates for assistant responses
 * Uses prepend() because column-reverse displays first child at bottom
 *
 * @param {string} message - Message text
 * @param {string} type - 'user' or 'assistant'
 * @param {string} timestamp - Optional timestamp (auto-generated if not provided)
 * @param {boolean} streaming - If true, update existing bubble instead of creating new one
 */
function addChatBubbleToCanvas(message, type = 'user', timestamp = null, streaming = false) {
  const chatMessages = document.getElementById('chat-messages');
  if (!chatMessages) {
    console.warn('[AppEvents] chat-messages element not found - may not be on home route');
    return;
  }

  // Generate timestamp if not provided
  if (!timestamp) {
    timestamp = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // For streaming responses, update the existing bubble if it exists
  if (streaming && type === 'assistant') {
    const firstBubble = chatMessages.firstElementChild;

    // Check if first bubble is an assistant response (streaming in progress)
    if (firstBubble && firstBubble.tagName.toLowerCase() === 'm5t-chat-response') {
      // Update existing bubble content
      firstBubble.setAttribute('message', message);
      console.log(`[AppEvents] Updated streaming ${type} bubble (${message.length} chars)`);
      return;
    }
  }

  // Create new bubble (user message or first streaming chunk)
  let bubble;
  if (type === 'user' || type === 'prompt') {
    bubble = document.createElement('m5t-chat-prompt');
  } else {
    bubble = document.createElement('m5t-chat-response');
  }

  // Set attributes
  bubble.setAttribute('message', message);
  bubble.setAttribute('timestamp', timestamp);

  // Prepend (add to top of DOM) - column-reverse displays it at bottom
  chatMessages.prepend(bubble);

  console.log(`[AppEvents] Added ${type} bubble to canvas (${message.length} chars)`);
}
